/**
 * Type definitions for Main Page feature
 * 
 * This file contains JSDoc type definitions for all data models used in the Main Page.
 * These types provide IDE autocomplete and type checking without requiring TypeScript.
 */

/**
 * @typedef {Object} Note
 * @property {string} id - Unique identifier for the note
 * @property {string} userId - ID of the user who owns this note
 * @property {string} title - Display name of the note
 * @property {number} flashcardCount - Number of flashcards in this note
 * @property {Date} createdAt - Timestamp when the note was created
 * @property {Date} updatedAt - Timestamp when the note was last modified
 */

/**
 * @typedef {Object} Flashcard
 * @property {string} id - Unique identifier for the flashcard
 * @property {string} noteId - ID of the parent note
 * @property {string} front - Question/prompt side of the flashcard
 * @property {string} back - Answer side of the flashcard
 * @property {Date|string} [createdAt] - Optional timestamp when the flashcard was created
 */

/**
 * @typedef {Object} UIState
 * @property {boolean} isLoading - Global loading indicator state
 * @property {string|null} error - Current error message (null if no error)
 * @property {Note[]} notes - Array of loaded notes
 * @property {boolean} hasFlashcards - Whether any flashcards exist in the database
 */

/**
 * @typedef {'database'|'navigation'|'import'|'session'} ErrorType
 */

/**
 * @typedef {Object} ErrorMessage
 * @property {ErrorType} type - Category of the error
 * @property {string} message - User-friendly error description
 * @property {string} [details] - Optional technical details
 * @property {Date} timestamp - When the error occurred
 * @property {boolean} dismissible - Whether the user can dismiss this error
 * @property {boolean} retryable - Whether the operation can be retried
 */

/**
 * @typedef {'note'|'random'} TestType
 */

/**
 * @typedef {'normal'|'completedReview'} PracticeMode
 */

/**
 * @typedef {Object} TestContext
 * @property {TestType} type - Type of test (note-specific or random)
 * @property {string} [noteId] - ID of the note (required for 'note' type)
 * @property {Flashcard[]} flashcards - Array of flashcards to include in the test
 * @property {Object} [settings] - Optional practice settings
 * @property {PracticeMode} [settings.practiceMode] - Optional practice mode
 */

/**
 * Type guard to check if a value is a valid Note
 * @param {any} value - Value to check
 * @returns {value is Note} True if value is a valid Note
 */
export function isNote(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.id === 'string' &&
    typeof value.userId === 'string' &&
    typeof value.title === 'string' &&
    typeof value.flashcardCount === 'number' &&
    value.createdAt instanceof Date &&
    value.updatedAt instanceof Date
  );
}

/**
 * Type guard to check if a value is a valid Flashcard
 * @param {any} value - Value to check
 * @returns {value is Flashcard} True if value is a valid Flashcard
 */
export function isFlashcard(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.id === 'string' &&
    typeof value.noteId === 'string' &&
    typeof value.front === 'string' &&
    typeof value.back === 'string' &&
    (
      value.createdAt === undefined ||
      value.createdAt === null ||
      value.createdAt instanceof Date ||
      typeof value.createdAt === 'string'
    )
  );
}

/**
 * Type guard to check if a value is a valid UIState
 * @param {any} value - Value to check
 * @returns {value is UIState} True if value is a valid UIState
 */
export function isUIState(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.isLoading === 'boolean' &&
    (value.error === null || typeof value.error === 'string') &&
    Array.isArray(value.notes) &&
    value.notes.every(isNote) &&
    typeof value.hasFlashcards === 'boolean'
  );
}

/**
 * Type guard to check if a value is a valid ErrorType
 * @param {any} value - Value to check
 * @returns {value is ErrorType} True if value is a valid ErrorType
 */
export function isErrorType(value) {
  return (
    typeof value === 'string' &&
    ['database', 'navigation', 'import', 'session'].includes(value)
  );
}

/**
 * Type guard to check if a value is a valid ErrorMessage
 * @param {any} value - Value to check
 * @returns {value is ErrorMessage} True if value is a valid ErrorMessage
 */
export function isErrorMessage(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    isErrorType(value.type) &&
    typeof value.message === 'string' &&
    (value.details === undefined || typeof value.details === 'string') &&
    value.timestamp instanceof Date &&
    typeof value.dismissible === 'boolean' &&
    typeof value.retryable === 'boolean'
  );
}

/**
 * Type guard to check if a value is a valid TestType
 * @param {any} value - Value to check
 * @returns {value is TestType} True if value is a valid TestType
 */
export function isTestType(value) {
  return (
    typeof value === 'string' &&
    ['note', 'random'].includes(value)
  );
}

/**
 * Type guard to check if a value is a valid PracticeMode
 * @param {any} value - Value to check
 * @returns {value is PracticeMode} True if value is a valid PracticeMode
 */
export function isPracticeMode(value) {
  return (
    typeof value === 'string' &&
    ['normal', 'completedReview'].includes(value)
  );
}

/**
 * Type guard to check if a value is a valid TestContext
 * @param {any} value - Value to check
 * @returns {value is TestContext} True if value is a valid TestContext
 */
export function isTestContext(value) {
  if (
    value === null ||
    typeof value !== 'object' ||
    !isTestType(value.type) ||
    !Array.isArray(value.flashcards) ||
    !value.flashcards.every(isFlashcard)
  ) {
    return false;
  }

  // For 'note' type, noteId is required
  if (value.type === 'note' && typeof value.noteId !== 'string') {
    return false;
  }

  if (
    value.settings?.practiceMode !== undefined &&
    !isPracticeMode(value.settings.practiceMode)
  ) {
    return false;
  }

  return true;
}

// Preserve the default export for compatibility with existing consumers.
const MainPageTypes = {};

export default MainPageTypes;
